import { sunglassesTarget } from "@/lib/unlockPace";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { playChime } from "@/lib/audio";
import { celebrateWin } from "@/lib/wins";
import { requestAvatarMessage, updateSettings } from "@/lib/api";
import { fireMochiNotification } from "@/lib/mochiNotifications";

// Manages an in-flight Pomodoro focus session for a reminder. Owns the
// session state machine (focus → break → null), chime audio, lifetime
// pomodoros_completed counter, and the proud celebration popup when focus
// completes.
//
//   const { pomodoro, startFocus, handleComplete, cancel } = usePomodoro({
//       settings, setSettings, onCheckin,
//   });
//
// `onCheckin(payload)` is invoked when the focus phase ends (proud popup) or
// the break ends (energetic popup) — host renders the CheckInPopup.
export function usePomodoro({ settings, setSettings, onCheckin, onFocusComplete }) {
    const [pomodoro, setPomodoro] = useState(null);

    const startFocus = useCallback((reminder, focusMin, breakMin) => {
        setPomodoro({
            reminderId: reminder.id,
            title: reminder.title,
            focusMin,
            breakMin,
            startedAt: Date.now(),
            phase: "focus",
            avatarStyle: settings?.avatar_style || "feminine",
        });
        const totalSec = Math.round(focusMin * 60);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        const label = s ? `${m}m ${s}s` : `${m} min`;
        toast.success(`Focusing on "${reminder.title}" for ${label} 🧠`);
        // playChime can throw if AudioContext isn't unlocked yet — harmless.
        try { playChime(); } catch { /* audio not yet unlocked — silent ok */ }
    }, [settings]);

    const handleComplete = useCallback(async (phase, session) => {
        try { playChime(); } catch { /* audio not yet unlocked — silent ok */ }

        if (phase === "focus") {
            celebrateWin("pomodoro");
            try { onFocusComplete?.(session.reminderId); } catch (e) { console.warn("focus-complete callback failed", e); }
            try {
                const curr = (settings?.pomodoros_completed || 0) + 1;
                await updateSettings({ pomodoros_completed: curr });
                setSettings({ ...settings, pomodoros_completed: curr });
                const target = sunglassesTarget(settings);
                if (curr === target) {
                    toast(`🕶️ ${target} Pomodoros! Mochi just earned sunglasses (in spirit).`, { duration: 6000 });
                }
            } catch (e) { console.warn("pomodoro counter persist failed", e); }

            let body = `Focus session complete! Time for a ${session.breakMin}-min break ☕`;
            try {
                const r = await requestAvatarMessage("focus_complete", "proud", { task_title: session.title });
                if (r?.message) body = r.message;
            } catch (e) { console.warn("avatar message failed — using fallback line", e); }

            onCheckin?.({
                open: true, message: body, mood: "proud",
                kind: null, title: null, reminderId: null,
            });
            if ("Notification" in window && Notification.permission === "granted") {
                fireMochiNotification(
                    "pomodoro_complete",
                    { task_title: session.title },
                    { tag: `focus-${session.reminderId}` },
                );
            }

            if (session.breakMin > 0) {
                setPomodoro({ ...session, phase: "break", startedAt: Date.now() });
            } else {
                setPomodoro(null);
            }
        } else {
            onCheckin?.({
                open: true, message: "Break's over! Ready to keep going? ✨",
                mood: "energetic", kind: null, title: null, reminderId: null,
            });
            setPomodoro(null);
        }
    }, [settings, setSettings, onCheckin, onFocusComplete]);

    const cancel = useCallback(() => setPomodoro(null), []);

    return { pomodoro, startFocus, handleComplete, cancel };
}
